import { Howl } from 'howler';
import { AudioSpritesData, GameMusicIds, MUSIC_VOLUME, SOUND_VOLUME } from '../Config/Audio';
import Core from '../Core/Core';
import getLogger, { ILogger } from '../Core/Logger';
import axios from 'axios';

export const soundIds = new Map<string, Howl>();
const sfx = new Map<string, Howl>();
const music = new Map<string, Howl>();

let soundEnabled: boolean = true;
let musicEnabled: boolean = true;
let locked: boolean = false;
let isSilent: boolean = false;
let latestMusic: Object = null;

interface ISoundsPlaying {
    [key: string]: number[];
}

const soundsPlaying: ISoundsPlaying = {};

const soundLogger: ILogger = getLogger('[SOUND-MANAGER]:');

const SoundManager = {
    startGameSound() {
        const initS = new Howl({
            src: ['./assets/boot/sfx/tap.{mp3,ogg}'],
            loop: false,
            volume: 0,
        });
        soundIds.set('init-sound', initS);

        Promise.all([playStart()])
            .then(() => {
                this.playSound('init-sound');
            })
            .catch(err => {
                soundLogger.debug('error playing start sound: ' + err);
            });
    },

    playMusic(id: string, fade?: boolean, ms?: number) {
        if (isSilent) {
            latestMusic = { id, fade, ms };
        } else {
            this.playSound(id, null, fade, ms);
        }
    },

    silentMusic(shallSilent: boolean) {
        isSilent = shallSilent;
        if (shallSilent === false && latestMusic != null) {
            // @ts-ignore
            this.playMusic(latestMusic.id, latestMusic.fade, latestMusic.ms);
        }
    },

    getSpriteForId(id: string) {
        for (const spriteDataId in AudioSpritesData) {
            if (AudioSpritesData[spriteDataId].sprite[id]) {
                return soundIds.get(spriteDataId);
            }
        }
        return null;
    },
    playSound(id: string, complete?: () => void, fade?: boolean, ms?: number) {
        const isMusic = GameMusicIds.includes(id);
        const isSfx = !isMusic;
        const { soundFx, music: stateMusic } = Core.store.getState().StateSound;
        const sfxDisabled = !soundFx;
        const musicDisabled = !stateMusic;

        if (isSfx && sfxDisabled) {
            return;
        }
        if (music.has(id) && !musicDisabled) {
            const sprite = music.get(id);
            const musicInst = soundsPlaying[id][0];
            if (!sprite.playing(musicInst)) {
                sprite.play(musicInst);
            }
            return;
        }

        try {
            let playedSound: Howl = this.getSpriteForId(id);
            if (!playedSound || (playedSound.state() === 'unloaded' && isSfx)) {
                return;
            }

            const inst: number = playedSound.play(id);
            if (isMusic && musicDisabled) {
                playedSound.pause(inst);
            }

            soundsPlaying[id] = soundsPlaying[id] ? [...soundsPlaying[id], inst] : [inst];

            const vol: number = GameMusicIds.includes(id) ? MUSIC_VOLUME : SOUND_VOLUME;
            if (fade) {
                const fadetime = ms || 100;
                playedSound.fade(0, vol, fadetime, inst);
            }

            playedSound.once('end', () => {
                if (complete) {
                    complete();
                }

                if (!GameMusicIds.includes(id)) {
                    sfx.delete(id);
                    playedSound = null;
                }
            });

            if (isMusic) {
                music.set(id, playedSound);
                playedSound.loop(true, inst);
            } else {
                sfx.set(id, playedSound);
            }
        } catch (e) {
            console.error('Could not play sound', e);
        }
    },

    stopSound(id: string, fade?: boolean, ms?: number) {
        const sprite = this.getSpriteForId(id);
        const allPlaying = soundsPlaying[id];

        if (!sprite || !allPlaying || allPlaying.length === 0) {
            return;
        }

        allPlaying.forEach(inst => {
            if (fade && inst) {
                const fadetime = ms || 100;
                const vol: number = GameMusicIds.includes(id) ? MUSIC_VOLUME : SOUND_VOLUME;
                sprite.fade(vol, 0, fadetime, inst);
            } else {
                sprite.stop(inst);
            }
        });

        if (GameMusicIds.includes(id)) {
            music.delete(id);
        } else {
            sfx.delete(id);
        }

        soundsPlaying[id] = null;
    },

    setMusicEnabled(enable: boolean) {
        musicEnabled = enable;
        if (!musicEnabled) {
            this.stopAllMusic();
        } else {
            this.resumeAllMusic();
        }
    },

    setSoundEabled(enable: boolean) {
        soundEnabled = enable;
        if (!soundEnabled) {
            this.stopAllSounds();
        }
    },

    stopAllAudio(lock?: boolean) {
        if (lock) {
            locked = lock;
        }

        if (Howler.ctx && Howler.ctx.state === 'running') {
            Howler.ctx.suspend().catch(err => {
                soundLogger.debug('error with stopAll: ' + err);
            });
        }
    },

    resumeAllAudio(unlock?: boolean) {
        if (locked && !unlock) {
            return;
        } else if (unlock) {
            locked = false;
        }

        if (Howler.ctx && Howler.ctx.state !== 'running') {
            Howler.ctx.resume().catch(err => {
                soundLogger.debug('error with resumeAll: ' + err);
            });
        }
    },

    stopAllMusic() {
        music.forEach(m => {
            m.stop();
        });
    },

    resumeAllMusic() {
        const keys = music.keys();
        music.forEach(() => {
            this.playMusic(keys.next().value, true, 100);
        });
    },

    stopAllSounds() {
        sfx.forEach(s => {
            s.stop();
        });
    },

    handleFade() {
        if (this.volume() === 0) {
            this.stop();
        }
    },
};

export default SoundManager;

// function passed to loader so sounds are loaded by howler
export const HowlerLoader = {
    async pre(resource: PIXI.loaders.Resource, next: () => void) {
        const { extension, url } = resource;
        if (soundIds.get(resource.name)) {
            const dun = soundIds.get(resource.name);
            dun.unload();
            soundIds.set(resource.name, null);
        }
        if (extension === 'mp3' || extension === 'ogg') {
            const key = resource.name;
            const separator = url.lastIndexOf('.');
            const jsonUrl = `${url.substring(0, separator)}.json`;
            const spriteData = (await axios.get(jsonUrl)).data;
            AudioSpritesData[key] = spriteData;
            const options: IHowlProperties = {
                ...resource.metadata,
                src: spriteData.urls,
                sprite: spriteData.sprite,
                loop: GameMusicIds.includes(resource.name),
                onfade: SoundManager.handleFade,
                onload: () => {
                    resource.complete();
                    next();
                },
                volume: GameMusicIds.includes(resource.name) ? SOUND_VOLUME : MUSIC_VOLUME,
            };
            const h = new Howl(options);
            soundIds.set(resource.name, h);
        } else {
            next();
        }
    },
};

function playStart() {
    return new Promise<void>(res => setTimeout(res, 800));
}
